<template>
    
  <v-card>
    <!-- TOOLBAR (Fullscreen Header) -->
    <v-toolbar color="primary" dark flat dense class="flex-grow-0 flex-shrink-0">
      <v-icon left>{{ icons.mdiDatabaseSync }}</v-icon>
      <v-toolbar-title class="font-weight-bold">Datamap Checker & Mapping <span v-if="dbMappingStatus"> - Finalized <v-icon left>{{ icons.mdiCheckCircle }}</v-icon></span></v-toolbar-title>
      <v-spacer></v-spacer>
      
      <!-- Show Path in Toolbar if it exists -->
      <v-chip 
        v-if="!needsPathSetup && decipherPath" 
        color="primary darken-2" 
        class="mr-4 font-monospace"
        style="cursor: pointer;"
        @click="openEditPathDialog"
         :disabled="dbMappingStatus"
      >
        <v-icon left small>{{ icons.mdiFolderSearchOutline }}</v-icon>
        {{ decipherPath }}
        <v-icon right small class="ml-2">{{ icons.mdiPencil }}</v-icon>
      </v-chip>

      <v-btn icon @click="$emit('close')">
        <v-icon>{{ icons.mdiClose }}</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card flat class="mb-0"  :disabled="dbMappingStatus">
      <!-- STATE 1: SETUP PROMPT (No mapping or path exists) -->
    <v-container v-if="needsPathSetup" class="fill-height d-flex align-center justify-center">
      <v-card max-width="500" width="100%" outlined class="text-center pa-6 elevation-2">
        <v-card-title class="pa-0">
            <v-icon color="primary" size="64" class="mb-4">{{ icons.mdiLinkVariant }}</v-icon>
            <h3 class="text-h5 font-weight-bold mb-2">Link Decipher Survey</h3>
          <p class="grey--text text-body-2 mb-6">
            No Decipher survey path is configured for this country yet. Enter the path below to initialize the datamapper.
          </p>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-text-field
            v-model="newDecipherPath"
            label="Decipher Project Path"
            placeholder="e.g. selfserve/9d3/12345"
            outlined
            dense
            hide-details="auto"
            class="mb-4 font-monospace"
          ></v-text-field>

          <v-btn 
            color="primary" 
            block 
            small 
            :disabled="!newDecipherPath || newDecipherPath.length < 5"
            :loading="isSettingUp"
            @click="initializeMapping"
          >
            Save Path & Start Mapping
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- STATE 2: LOADING / MAPPING UI -->
    <div v-else class="flex-grow-1 d-flex flex-column position-relative bg-grey-lighten-5">
      
      <!-- Loading Overlay -->
      <v-overlay :value="isChecking" absolute color="white" opacity="0.9" z-index="5">
        <div class="d-flex flex-column align-center justify-center text-center">
          <v-progress-circular indeterminate color="primary" size="64" width="6" class="mb-6"></v-progress-circular>
          <transition name="fade" mode="out-in">
            <h3 :key="currentLoadingText" class="text-h6 primary--text font-weight-medium">
              {{ currentLoadingText }}
            </h3>
          </transition>
          <p class="text-body-2 grey--text mt-2 max-w-sm">
            Parsing datamap structure and mapping expected labels...
          </p>
        </div>
      </v-overlay>

      <v-container fluid class="pa-4 flex-grow-1 d-flex flex-column">
        
        <!-- TOP ACTIONS & STATS BAR -->
        <v-card outlined class="mb-4">
          <v-card-title class="py-3 px-4 d-flex align-center flex-wrap" style="gap: 16px;">
            <!-- Filters -->
            <v-chip-group v-model="statusFilter" active-class="primary--text" mandatory>
              <v-chip value="exact" small color="success" outlined>Exact ({{ exactCount }})</v-chip>
              <v-chip value="mapped" small color="primary" outlined>Mapped ({{ mappedCount }})</v-chip>
              <v-chip value="suggested" small color="warning" outlined>Suggested ({{ suggestedCount }})</v-chip>
              <v-chip value="unmapped" small color="error" outlined>Unmapped ({{ unmappedCount }})</v-chip>
              <v-chip value="all" small>All ({{ results.length }})</v-chip>
            </v-chip-group>

            <v-spacer></v-spacer>

            <!-- Actions -->
            <v-btn color="warning" outlined @click="exportJSON" :disabled="hasBlockingErrors">
              <v-icon left>{{ icons.mdiCodeJson }}</v-icon> Export JSON
            </v-btn>
            
            <v-divider vertical class="mx-2"></v-divider>

            <v-btn color="primary" outlined @click="saveToDatabase(false)" :loading="isSaving">
              <v-icon left>{{ icons.mdiContentSave }}</v-icon> Save Draft
            </v-btn>
            <v-btn color="success" @click="saveToDatabase(true)" :loading="isSaving" :disabled="hasBlockingErrors">
              <v-icon left>{{ icons.mdiCheckDecagram }}</v-icon> Finalize & Lock
            </v-btn>
          </v-card-title>
        </v-card>

        <!-- WARNINGS & EXPANSION PANELS -->
        <v-expansion-panels flat outlined class="mb-4">
          <v-expansion-panel>
            <v-expansion-panel-header class="font-weight-medium py-2 min-h-0 text-caption">
              View Unmapped Expected Labels ({{ UnmappedLabels.length }})
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="d-flex flex-wrap mt-2">
                <v-chip v-for="(label, idx) in UnmappedLabels" :key="idx" small color="primary" outlined class="ma-1 font-monospace text-caption">
                  {{ label }}
                </v-chip>
                <span v-if="UnmappedLabels.length === 0" class="grey--text text-caption">All labels mapped!</span>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
          
          <v-expansion-panel v-if="flaggedItems.length > 0" :value="0">
            <v-expansion-panel-header class="font-weight-bold warning--text py-2 min-h-0 text-subtitle-2">
              <div class="d-flex align-center">
                <v-icon left color="warning" small class="mr-2">{{ icons.mdiFlag }}</v-icon>
                Flagged Variables & Near Matches ({{ flaggedItems.length }})
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="bg-grey-lighten-5 pt-3">
              <v-list dense class="transparent">
                <v-list-item v-for="(item, idx) in flaggedItems" :key="idx" class="px-0 py-2 border-bottom-dashed align-start">
                  
                  <v-list-item-content class="py-0" style="max-width: 25%;">
                    <v-list-item-title class="font-monospace font-weight-bold">{{ item.datamap_label }}</v-list-item-title>
                  </v-list-item-content>
                  
                  <v-list-item-content class="py-0" style="max-width: 15%;">
                       <v-chip x-small :color="getStatusColor(item.status)" :text-color="['warning', 'error'].includes(getStatusColor(item.status)) ? 'black' : 'white'" label>
                          {{ item.status.toUpperCase() }}
                        </v-chip>
                  </v-list-item-content>
                  
                  <v-list-item-content class="py-0 px-3" style="max-width: 30%;">
                    <div v-if="item.title" class="mb-2 text-caption lh-tight v-html-safe">{{ item.title }}</div>
                  </v-list-item-content>

                  <v-list-item-action class="ma-0 py-0" style="width: 30%;">
                    <v-autocomplete
                      v-model="item.mapped_to"
                      :items="dropdownOptions"
                      placeholder="Select expected label..."
                      dense outlined hide-details clearable
                      class="font-monospace w-100"
                      @change="onMappingChange(item)"
                    >
                      <template #item="{ item: dropdownItem, attrs, on }">
                        <v-list-item v-bind="attrs" v-on="on" :disabled="dropdownItem.disabled">
                          <v-list-item-content>
                            <v-list-item-title :class="{'grey--text': dropdownItem.disabled}">{{ dropdownItem.text }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- MAIN DATA TABLE -->
        <v-card outlined class="flex-grow-1 d-flex flex-column overflow-hidden">
          <v-data-table
            :headers="headers"
            :items="filteredResults"
            :items-per-page="-1"
            hide-default-footer
            dense
            fixed-header
            height="100%"
            class="elevation-0 flex-grow-1"
          >
            <template #item.datamap_label="{ item }">
              <span class="font-monospace font-weight-medium">{{ item.datamap_label }}</span>
            </template>

            <template #item.status="{ item }">
              <v-chip :color="getStatusColor(item.status)" :text-color="getStatusColor(item.status) === 'warning' ? 'black' : 'white'" small label>
                <v-icon left small>{{ getStatusIcon(item.status) }}</v-icon>
                {{ item.status.toUpperCase() }}
              </v-chip>
            </template>

            <template #item.mapped_to="{ item }">
              <v-autocomplete
                v-model="item.mapped_to"
                :items="dropdownOptions"
                placeholder="Select an expected label..."
                dense outlined hide-details clearable
                class="my-1 font-monospace"
                style="max-width: 350px;"
                @change="onMappingChange(item)"
              >
                <template #item="{ item: dropdownItem, attrs, on }">
                  <v-list-item v-bind="attrs" v-on="on" :disabled="dropdownItem.disabled">
                    <v-list-item-content>
                      <v-list-item-title :class="{'grey--text': dropdownItem.disabled}">{{ dropdownItem.text }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action v-if="dropdownItem.disabled">
                      <v-chip x-small color="grey lighten-2">Assigned</v-chip>
                    </v-list-item-action>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </template>

            <template #item.warnings="{ item }">
              <div v-if="item.warnings && item.warnings.length > 0">
                <v-tooltip bottom v-for="(warn, idx) in item.warnings" :key="warn.id">
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip 
                      v-bind="attrs" v-on="on"
                      :color="warn.type === 'error' ? 'error' : 'warning'" 
                      :text-color="warn.type === 'error' ? 'white' : 'black'"
                      small close class="mb-1 mr-1"
                      @click:close="removeWarning(item, idx)"
                    >
                      <v-icon left small>{{ warn.type === 'error' ? icons.mdiAlertCircle : icons.mdiAlert }}</v-icon>
                      {{ warn.text }}
                    </v-chip>
                  </template>
                  <div class="pa-1">
                    <div class="font-weight-bold mb-1 border-bottom">Datamap Choices (Values):</div>
                    <template v-if="warn.raw_values && warn.raw_values.length > 0">
                      <div v-for="(val, vIdx) in warn.raw_values" :key="vIdx" class="text-caption">
                        <span class="font-weight-bold">[{{ val.value }}]</span> {{ val.title || val.label }}
                      </div>
                    </template>
                    <div v-else class="text-caption font-italic mt-1">No choice values found.</div>
                  </div>
                </v-tooltip>
              </div>
              <span v-else class="grey--text text-caption">-</span>
            </template>
          </v-data-table>
        </v-card>
      </v-container>
    </div>
    <!-- Edit Decipher Path Dialog -->
    <v-dialog v-model="editPathDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 grey lighten-4">Edit Decipher Path</v-card-title>
        <v-card-text>
          <p class="text-body-2 grey--text mb-4">
            Updating the path will automatically fetch the new survey's datamap and attempt to cross-reference your existing mapping.
          </p>
          <v-text-field
            v-model="editPathValue"
            label="Decipher Project Path"
            placeholder="e.g. selfserve/9d3/12345"
            outlined
            dense
            hide-details="auto"
            class="font-monospace"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="editPathDialog = false" :disabled="isSavingPath">Cancel</v-btn>
          <v-btn color="primary" @click="updateDecipherPath" :loading="isSavingPath" :disabled="!editPathValue">
            Update & Reload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </v-card>
    
  </v-card>
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import useProjectView from '../useProjectView'
import {
    mdiClose, mdiDatabaseSync, mdiFolderSearchOutline, mdiLinkVariant,
    mdiCheckCircle, mdiCodeJson, mdiRefresh, mdiContentSave, mdiPencil,
    mdiAlertCircle, mdiCheckDecagram, mdiAlert, mdiFlag, mdiHelpCircle
} from '@mdi/js'

export default {
  name: 'DatamapChecker',
  props: {
    countryId: {
      type: [Number, String],
      required: true 
    }
  },
  setup(props, { emit }) {
    const { fetchDatamap, fetchDatamapMapping, saveDatamapMapping, createMapping } = useProjectView()

    // Setup / State Variables
    const decipherPath = ref('')
    const needsPathSetup = ref(true)
    const newDecipherPath = ref('')
    const isSettingUp = ref(false)

    const editPathDialog = ref(false)
    const editPathValue = ref('')
    const isSavingPath = ref(false)

    const openEditPathDialog = () => {
      editPathValue.value = decipherPath.value
      editPathDialog.value = true
    }

    const updateDecipherPath = async () => {
      isSavingPath.value = true
      try {
        // We can safely reuse createMapping. 
        // Our backend uses get_or_create, so it updates the ProjectCountry path 
        // without erasing any existing mappings saved in ODBDatamapMapping!
        await createMapping({
          country_id: props.countryId,
          decipher_path: editPathValue.value
        })
        
        decipherPath.value = editPathValue.value
        editPathDialog.value = false
        
        // Clear old results and reload the validation using the new path
        results.value = []
        await runCheck() 
      } catch (e) {
        console.error("Failed to update path:", e)
      } finally {
        isSavingPath.value = false
      }
    }

    const defaultLabelsArray = [
        {"label":"uuid"},
      {"label":"db_ID_pack"},
      {"label":"vShopTimeSeconds"},
      {"label":"O_ShoppingObservation1","scale":2},
      {"label":"O_ShoppingObservation2","scale":4},
      {"label":"CategoryDriverOE"},
      {"label":"P_VisualCues_COLORS"},
      {"label":"P_VisualCues_SHAPE"},
      {"label":"P_VisualCues_LOGO"},
      {"label":"P_VisualCues_VARIANT"},
      {"label":"P_VisualCues_INGREDIENTS"},
      {"label":"P_VisualCues_VISUALS"},
      {"label":"P_ShoppingMotivationOE"},
      {"label":"P_ShoppingBarriersOE"},
      {"label":"P_ShoppingBarriers_OTHER_BRAND"},
      {"label":"P_ShoppingBarriers_UNFAMILIAR"},
      {"label":"P_ShoppingBarriers_USUAL"},
      {"label":"P_ShoppingBarriers_NOTICE"},
      {"label":"P_ShoppingBarriers_DESINTEREST"},
      {"label":"P_ShoppingBarriers_UNDERSTAND"},
      {"label":"P_ShoppingBarriers_BRAND"},
      {"label":"P_ShoppingBarriers_PRODUCT"},
      {"label":"P_ShoppingBarriers_EXPENSIVE"},
      {"label":"P_ShoppingBarriers_UNAPPEALING"},
      {"label":"P_ShoppingBarriers_MEET_NEEDS"},
      {"label":"vFindCorrect"},
      {"label":"FindTimeSeconds"},
      {"label":"vFindTimeSeconds"},
      {"label":"quantity_test"},
      {"label":"quantity_range"},
      {"label":"quantity_total"},
      {"label":"turnover_test"},
      {"label":"turnover_range"},
      {"label":"turnover_total"},
      {"label":"EaseOfFind","scale":5},
      {"label":"vEaseOfFind","scale":5},
      {"label":"F_Flash1_OE"},
      {"label":"F_Flash2_OE"},
      {"label":"F_Flash_Recall","scale":2},
      {"label":"F_FLASH3_FIT","scale":10},
      {"label":"F_FLASH3_UND","scale":10},
      {"label":"F_FLASH3_USUAL","scale":10},
      {"label":"F_FLASH3_QUAL","scale":10},
      {"label":"F_FLASH3_CLEAR","scale":10},
      {"label":"F_FLASH3_EXPENSIVE","scale":10},
      {"label":"F_FLASH3_TASTE","scale":10},
      {"label":"F_FLASH3_HEALTHY","scale":10},
      {"label":"F_FLASH3_EFFECTIVE","scale":10},
      {"label":"F_FLASH3_SUITABLE","scale":10},
      {"label":"F_FLASH3_WANTBUY","scale":10},
      {"label":"F_Emotion","scale":10},
      {"label":"F_DifferentiationAfterFlash","scale":5},
      {"label":"Q_InitialImpressionsOE"},
      {"label":"Q_OA_5pts","scale":5},
      {"label":"Q_OA_4pts","scale":4},
      {"label":"Q_DESIGNAPPEAL_DESIGN","scale":4},
      {"label":"Q_DESIGNAPPEAL_COLORS","scale":4},
      {"label":"Q_DESIGNAPPEAL_BRAND","scale":4},
      {"label":"Q_DESIGNAPPEAL_NAME","scale":4},
      {"label":"Q_DESIGNAPPEAL_PRODUCTFONT","scale":4},
      {"label":"Q_DESIGNAPPEAL_VARIANTFONT","scale":4},
      {"label":"Q_DESIGNAPPEAL_INGREDIENTS","scale":4},
      {"label":"Q_DESIGNAPPEAL_PICTURE","scale":4},
      {"label":"Q_DESIGNAPPEAL_DRAWING","scale":4},
      {"label":"Q_DESIGNAPPEAL_CLAIM","scale":4},
      {"label":"Q_DESIGNAPPEAL_6pts_DESIGN","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_COLORS","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_BRAND","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_NAME","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_PRODUCTFONT","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_VARIANTFONT","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_INGREDIENTS","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_PICTURE","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_DRAWING","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_CLAIM","scale":6},
      {"label":"Q_PACKATT_PREM"},
      {"label":"Q_PACKATT_UNIQUE"},
      {"label":"Q_PACKATT_MODERN"},
      {"label":"Q_PACKATT_APP"},
      {"label":"Q_PACKATT_UND"},
      {"label":"Q_PACKATT_EXCITING"},
      {"label":"Q_PACKATT_INFORM"},
      {"label":"Q_PACKATT_CLUTTER"},
      {"label":"Q_PACKATT_CONFUSE"},
      {"label":"Q_PACKATT_OUT"},
      {"label":"Q_PACKATT_None"},
      {"label":"Q_COMMUNICATION_QUALITY","scale":5},
      {"label":"Q_COMMUNICATION_LIKEME","scale":5},
      {"label":"Q_COMMUNICATION_TRUST","scale":5},
      {"label":"Q_COMMUNICATION_BETTER","scale":5},
      {"label":"Q_COMMUNICATION_HEALTHY","scale":5},
      {"label":"Q_COMMUNICATION_TASTE","scale":5},
      {"label":"Q_COMMUNICATION_EFFECT","scale":5},
      {"label":"Q_COMMUNICATION_ECO","scale":5},
      {"label":"Q_COMMUNICATION2_TEST_VALUE","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_QUALITY","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_TASTE","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_EXPENSIVE","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_MODERN","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_APPEAL","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_TRUSTWORTHY","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_RELEVANCE","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_EFFECTIVE","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_SUITABLE","scale":10},
      {"label":"Q_OverallLiking_TEST","scale":10},
      {"label":"vF_IATQUALITY"},
      {"label":"vF_IATTRUST"},
      {"label":"vF_IATMODERN"},
      {"label":"vF_IATUNIQUE"},
      {"label":"vF_IATTASTE"},
      {"label":"vF_IATPACKAGING"},
      {"label":"vF_IATPURCHASE"},
      {"label":"Q_PropensityToBuy_Unpriced","scale":5},
      {"label":"Q_PropensityToBuy","scale":5},
      {"label":"Q_Price_Perception","scale":5},
      {"label":"Q_Value","scale":5},
      {"label":"Q_PI","scale":5},
      {"label":"Q_Fit_Concept","scale":4},
      {"label":"Q_Benefit_PostConcept","scale":3},
      {"label":"Q_PI_postconcept","scale":3},
      {"label":"Q_Fit_brand","scale":4},
      {"label":"Q_different","scale":4},
      {"label":"Q_Benefit","scale":4},
      {"label":"Q_Benefit2","scale":3},
      {"label":"Q_BenefitOE"},
      {"label":"Q_Meets_needs","scale":3},
      {"label":"Q_Missing_prod","scale":3},
      {"label":"w_main"},
      {"label":"w_user"},
      {"label":"db_rep_sample","scale":2},
      {"label":"db_cell_ad","scale":2},
      {"label":"db_written_concept","scale":2},
      {"label":"db_past_purchased","scale":2},
      {"label":"db_bumo","scale":2},
      {"label":"db_sample","scale":2},
      {"label":"DeviceUsed","scale":4},
      {"label":"S_Gender","scale":4},
      {"label":"S_Age"},
      {"label":"S_FrequencyTB","scale":7},
      {"label":"S_Brand_PastPurchasedTB"},
      {"label":"S_Brand_BUMOTB"},
      {"label":"S_HHsize","scale":6},
      {"label":"S_NbChildren","scale":7},
      {"label":"S_AgeChildrenr1"},
      {"label":"S_AgeChildrenr2"},
      {"label":"S_AgeChildrenr3"},
      {"label":"S_AgeChildrenr4"},
      {"label":"S_AgeChildrenr5"},
      {"label":"S_AgeChildrenr6"},
      {"label":"S_AgeChildrenr7"},
      {"label":"S_AgeChildrenr8"},
      {"label":"S_AgeChildrenr9"},
      {"label":"S_AgeChildrenr10"},
      {"label":"S_AgeChildrenr11"},
      {"label":"S_AgeChildrenr12"},
      {"label":"S_AgeChildrenr13"},
      {"label":"S_AgeChildrenr14"},
      {"label":"S_AgeChildrenr15"},
      {"label":"S_AgeChildrenr16"},
      {"label":"S_AgeChildrenr17"},
      {"label":"S_AgeChildrenr18"},
      {"label":"S_CurrentHair_Color","scale":7},
      {"label":"S_Hair_Length","scale":7},
      {"label":"S_Hair_Texture_6","scale":6},
      {"label":"S_Hair_Texture_8","scale":8},
      {"label":"C_Marital","scale":6},
      {"label":"C_Education_US","scale":7},
      {"label":"C_Education_UK","scale":7},
      {"label":"C_Education_GER","scale":7},
      {"label":"C_Employment","scale":8},
      {"label":"C_Profession","scale":8},
      {"label":"C_Agglo","scale":4},
      {"label":"C_Ethnicity","scale":7},
      {"label":"C_Hispanic","scale":6},
      {"label":"C_State","scale":51},
      {"label":"C_Store","scale":5},
      {"label":"db_test_vs_comp_eval_order","scale":3},
      {"label":"R_UnaidedBrandOE"},
      {"label":"vUnaidedBrand"},
      {"label":"vAidedRecallTB"},
      {"label":"vAidedRecallRangeTB"},
      {"label":"vAidedRecallRange_wo_innoTB"},
      {"label":"quantity_range_wo_inno"},
      {"label":"P_Click"},
      {"label":"P_PickUp"},
      {"label":"P_BOP"},
      {"label":"P_SOP"},
      {"label":"P_Smell"},
      {"label":"Q_PACKATT_NET_POSITIVE"},
      {"label":"Q_PACKATT_NET_NEGATIVE"},
      {"label":"C_Income_US","scale":7},
      {"label":"C_Income_UK","scale":14},
      {"label":"C_Income_EUR","scale":14}
    ]
    const isChecking = ref(false)
    const isSaving = ref(false)
    const results = ref([])
    const statusFilter = ref('exact')
    const dbMappingStatus = ref(null)

    const headers = [
      { text: 'Datamap Field', value: 'datamap_label', width: '25%' },
      { text: 'Status', value: 'status', width: '15%', align: 'center' },
      { text: 'Expected Label', value: 'mapped_to', width: '30%' },
      { text: 'Warnings', value: 'warnings', width: '30%', sortable: false },
    ]

    const parsedExpectedLabels = computed(() => defaultLabelsArray)

    // INIT FUNCTION: Checks DB on mount
    const initialize = async () => {
      isChecking.value = true
      try {
        const savedDBMapping = await fetchDatamapMapping(props.countryId)
        
        if (savedDBMapping && savedDBMapping.decipher_path) {
          decipherPath.value = savedDBMapping.decipher_path
          dbMappingStatus.value = savedDBMapping.is_final
          needsPathSetup.value = false
          await runCheck(savedDBMapping) // Proceed directly to mapping
        } else {
          needsPathSetup.value = true // Show setup prompt
        }
      } catch (e) {
        needsPathSetup.value = true
      } finally {
        isChecking.value = false
      }
    }

    // Handles the setup prompt save button
    const initializeMapping = async () => {
      isSettingUp.value = true
      try {
        // Creates the DB row AND sets the path on the ProjectCountry
        await createMapping({
          country_id: props.countryId,
          decipher_path: newDecipherPath.value
        })
        decipherPath.value = newDecipherPath.value
        needsPathSetup.value = false
        await runCheck(null)
      } catch (e) {
        console.error("Failed to initialize mapping:", e)
      } finally {
        isSettingUp.value = false
      }
    }

    // MAIN CHECKER LOGIC
    const runCheck = async (preloadedMapping = null) => {
      isChecking.value = true
      startLoadingAnimation()

      try {
        // Fetch existing mapping if we didn't pass it from initialize()
        const savedDBMapping = preloadedMapping || await fetchDatamapMapping(props.countryId)

        // 1. Fetch Forsta Datamap
        const resp = await fetchDatamap({ path: decipherPath.value })
        const rawDatamap = resp.variables
        const availableDmItems = extractAllLabelsWithValues(rawDatamap)
        const initResults = availableDmItems.map(dm => ({
            datamap_label: dm.datamap_label, 
            type: dm.type,
            raw_values: dm.raw_values,
            vgroup: dm.vgroup,
            title: dm.title,
            mapped_to: null, 
            status: 'unmapped',
            warnings: []
        }))

        let remainingExpectedStr = [...new Set(parsedExpectedLabels.value.map(e => e.label))]

        // PASS 1: Exact
        initResults.forEach(row => {
            const matchIndex = remainingExpectedStr.findIndex(e => e === row.datamap_label)
            if (matchIndex !== -1) {
                row.mapped_to = remainingExpectedStr[matchIndex]
                row.status = 'exact'
                row.warnings = checkWarnings(row.mapped_to, row.raw_values)
                remainingExpectedStr.splice(matchIndex, 1)
            }
        })
        // PASS 2: Prefix Matches
        initResults.filter(r => r.status === 'unmapped').forEach(row => {
            const matchIndex = remainingExpectedStr.findIndex(e => row.datamap_label.startsWith(e) && e !== row.datamap_label)
            if (matchIndex !== -1) {
                row.mapped_to = remainingExpectedStr[matchIndex]
                row.status = 'mapped'
                row.warnings = checkWarnings(row.mapped_to, row.raw_values)
                remainingExpectedStr.splice(matchIndex, 1)
            }
        })
        // PASS 3: Fuzzy Matches
        initResults.filter(r => r.status === 'unmapped').forEach(row => {
            const dmLower = row.datamap_label.toLowerCase()
            let bestIdx = -1
            let bestScore = Infinity

            remainingExpectedStr.forEach((e, idx) => {
                const eLower = e.toLowerCase()
                if (eLower.includes(dmLower) || dmLower.includes(eLower)) {
                    bestIdx = idx
                    bestScore = 0
                } else {
                    const dist = levenshtein(eLower, dmLower)
                    if (dist <= 3 && dist < bestScore) {
                        bestScore = dist
                        bestIdx = idx
                    }
                }
            })

            if (bestIdx !== -1 && bestScore <= 3) {
                row.mapped_to = remainingExpectedStr[bestIdx]
                row.status = 'suggested'
                row.warnings = checkWarnings(row.mapped_to, row.raw_values)
                remainingExpectedStr.splice(bestIdx, 1)
            }
        })

        // PASS 4: Database Overrides
        if (savedDBMapping && savedDBMapping.mapping_data) {
          savedDBMapping.mapping_data.forEach(dbItem => {
             const row = initResults.find(r => r.datamap_label === dbItem.label)
             if (row) {
                row.mapped_to = dbItem.expected_label
                row.status = dbItem.status
                row.warnings = checkWarnings(row.mapped_to, row.raw_values)
                
                const idx = remainingExpectedStr.indexOf(dbItem.expected_label)
                if (idx > -1) remainingExpectedStr.splice(idx, 1)
             }
          })
        }

        results.value = initResults
        statusFilter.value = 'exact'

      } catch (error) {
        console.error('Failed to validate datamap:', error)
      } finally {
        stopLoadingAnimation()
        isChecking.value = false
      }
    }

    const saveToDatabase = async (isFinal = false) => {
      isSaving.value = true
      try {
        const exportedVariables = results.value
          .filter(item => ['exact', 'mapped', 'suggested'].includes(item.status))
          .map(item => {
            const exportObj = {
              type: item.type,
              label: item.datamap_label,
              expected_label: item.mapped_to,
              status: item.status
            }
            if (item.raw_values && item.raw_values.length > 0) {
              const length = item.raw_values.length
              const firstValue = item.raw_values[0].value
              if (firstValue !== 1 && firstValue === length) {
                exportObj.reverse_scale = length
              }
            }
            return exportObj
          })

        await saveDatamapMapping({
          project_country: props.countryId,
          mapping_data: exportedVariables,
          is_final: isFinal
        })
        
        dbMappingStatus.value = isFinal
      } catch (e) {
        console.log(e)
      } finally {
        isSaving.value = false
      }
    }

    // Include your existing helpers here: getStatusColor, checkWarnings, etc...
    // (Truncated for brevity, paste your exact checkWarnings, getStatusColor, extractAllLabelsWithValues functions here)
    const checkWarnings = (mappedLabel, actualValues) => {
      const warnings = []
      if (!mappedLabel) return warnings

      const expectedObj = parsedExpectedLabels.value.find(e => e.label === mappedLabel)
      const expectedScale = expectedObj ? expectedObj.scale : null

      const hasValues = actualValues && Array.isArray(actualValues) && actualValues.length > 0
      const actualLength = hasValues ? actualValues.length : 0

      // Error: Scale Mismatch
      if (expectedScale && actualLength !== expectedScale) {
        warnings.push({
          id: `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: 'error',
          text: `Check scale: Expected ${expectedScale}, found ${actualLength}`,
          raw_values: actualValues
        })
      }

      // Warning: Reversed Scale
      if (hasValues) {
        const firstValue = actualValues[0].value
        if (firstValue !== 1 && firstValue === actualLength) {
          warnings.push({
            id: `warn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: 'warning',
            text: `Reversed scale: Starts with ${firstValue}`,
            raw_values: actualValues
          })
        }
      }

      return warnings
    }

    const getStatusColor = (status) => { return status === 'exact' ? 'success' : status === 'mapped' ? 'primary' : status === 'suggested' ? 'warning' : 'error' }
    const getStatusIcon = (status) => { return status === 'exact' ? mdiCheckDecagram : status === 'unmapped' ? mdiClose : mdiHelpCircle }
    const removeWarning = (item, warnIndex) => { item.warnings.splice(warnIndex, 1) }
    const onMappingChange = (item) => {
      if (item.mapped_to) item.status = item.mapped_to === item.datamap_label ? 'exact' : 'mapped'
      else item.status = 'unmapped'
      item.warnings = checkWarnings(item.mapped_to, item.raw_values)
    }
    const levenshtein = (a, b) => {
      const matrix = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
      for (let i = 0; i <= a.length; i++) matrix[i][0] = i;
      for (let j = 0; j <= b.length; j++) matrix[0][j] = j;
      for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          matrix[i][j] = Math.min(matrix[i - 1][j] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j - 1] + cost);
        }
      }
      return matrix[a.length][b.length];
    }

    const extractAllLabelsWithValues = (datamapArray) => {
      const map = new Map()
      const vgroupMap = new Map()

      // Pass 1: Build a registry mapping vgroup prefixes to all siblings
      datamapArray.forEach(q => {
        if (q.vgroup) {
          if (!vgroupMap.has(q.vgroup)) vgroupMap.set(q.vgroup, [])
          vgroupMap.get(q.vgroup).push({
            label: q.label,
            subId: q.row || q.col || q.choice || null,
            title: q.colTitle || q.rowTitle || q.title || q.qtitle || ''
          })
        }
      })

      // Pass 2: Process actual datamap into items
      datamapArray.forEach(question => {
        const parentValues = question.values || []
        const parentRows = question.rows || []
        const parentCols = question.cols || question.choices || question.answers || question.elements || []
        const parentType = question.type || 'text'
        const siblings = question.vgroup ? vgroupMap.get(question.vgroup) : []
        
        if (question.label) {
          map.set(question.label.trim(), { 
            values: parentValues, 
            type: parentType,
            vgroup: question.vgroup || null,
            title: question.colTitle || question.rowTitle || question.title || question.qtitle,
            parentRows: parentRows,
            parentCols: parentCols,
            siblings: siblings,
            subId: question.row || question.col || question.choice || null
          })
        }
        
        if (question.variables && Array.isArray(question.variables)) {
          question.variables.forEach(v => {
            if (v.label) {
              const vSiblings = v.vgroup ? vgroupMap.get(v.vgroup) : siblings
              map.set(v.label.trim(), { 
                values: v.values || parentValues, 
                type: v.type || parentType,
                vgroup: v.vgroup || question.vgroup || null,
                title: v.colTitle || v.rowTitle || v.title || v.qtitle || question.rowTitle || question.colTitle || question.title || question.qtitle,
                parentRows: parentRows,
                parentCols: parentCols,
                siblings: vSiblings,
                subId: v.row || v.col || v.choice || null
              })
            }
          })
        }
      })
      
      return Array.from(map.entries()).map(([label, data]) => ({ 
        datamap_label: label, 
        raw_values: data.values,
        type: data.type,
        vgroup: data.vgroup,
        title: data.title,
        parentRows: data.parentRows,
        parentCols: data.parentCols,
        siblings: data.siblings,
        subId: data.subId
      }))
    }
    const exportJSON = () => {
      const exportedVariables = results.value
        .filter(item => ['exact', 'mapped', 'suggested'].includes(item.status))
        .map(item => {
          const exportObj = {
            type: item.type,
            label: item.datamap_label,
            expected_label: item.mapped_to,
            status: item.status
          }

          if (item.raw_values && item.raw_values.length > 0) {
            const length = item.raw_values.length
            const firstValue = item.raw_values[0].value
            if (firstValue !== 1 && firstValue === length) {
              exportObj.reverse_scale = length
            }
          }

          return exportObj
        })

      const finalJSON = { variables: exportedVariables }
      
      const blob = new Blob([JSON.stringify(finalJSON, null, 2)], { type: 'application/json;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      
      link.setAttribute('href', url)
      link.setAttribute('download', 'mapped_variables.json')
      link.style.visibility = 'hidden'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    const hasBlockingErrors = computed(() => {
      // Returns true if there is at least one active warning of type 'error'
      return results.value.some(r => r.warnings && r.warnings.some(w => w.type === 'error'))
    })

    const exportCSV = () => {
      if (results.value.length === 0) return

      let csvContent = '"Datamap Label","Mapped Expected Label","Warnings"\n'

      results.value.forEach(item => {
        const expected = item.mapped_to || 'UNMAPPED'
        const warnings = item.warnings && item.warnings.length > 0 
          ? item.warnings.map(w => w.text).join(' | ') 
          : ''
        const safeDm = item.datamap_label.replace(/"/g, '""')
        const safeExpected = expected.replace(/"/g, '""')
        const safeWarn = warnings.replace(/"/g, '""')

        csvContent += `"${safeDm}","${safeExpected}","${safeWarn}"\n`
      })

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      
      link.setAttribute('href', url)
      link.setAttribute('download', 'datamap_to_expected_mapping.csv')
      link.style.visibility = 'hidden'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    // Loading Animations
    const loadingMessages = ['Connecting to Decipher...', 'Retrieving datamap layout...', 'Cross-referencing labels...']
    const currentMessageIndex = ref(0)
    let loadingInterval = null
    const currentLoadingText = computed(() => loadingMessages[currentMessageIndex.value])
    const startLoadingAnimation = () => {
      currentMessageIndex.value = 0
      loadingInterval = setInterval(() => { if (currentMessageIndex.value < 2) currentMessageIndex.value++ }, 500)
    }
    const stopLoadingAnimation = () => clearInterval(loadingInterval)

    // Computeds
    const filteredResults = computed(() => statusFilter.value === 'all' ? results.value : results.value.filter(r => r.status === statusFilter.value))
    const exactCount = computed(() => results.value.filter(r => r.status === 'exact').length)
    const unmappedCount = computed(() => results.value.filter(r => r.status === 'unmapped').length)
    const suggestedCount = computed(() => results.value.filter(r => r.status === 'suggested').length)
    const mappedCount = computed(() => results.value.filter(r => r.status === 'mapped').length)
    const UnmappedLabels = computed(() => dropdownOptions.value.filter(r => !r.disabled).map(r => r.text))
    const flaggedItems = computed(() => {
      // Exact match list from Python
      const exactMatches = [
        "P_CategoryDriverOE", "Q_FitBrand", "Q_OA", 
        "O_ShoppingObservation", "R_UnaidedRecall"
      ]

      // Keyword "includes" list from Python
      const keywords = [
        "P_VisualCues_r", "P_ShoppingObservation", "P_ShoppingBarriers_r",
        "F_FLASH3_r", "Q_DESIGNAPPEAL_r", "Q_PACKATT_r", "Q_PackAtt_",
        "Q_Communication", "Q_COMMUNICATION2_c", "Q_COMMUNICATION2_r",
        "Q_COMMUNICATION2_TEST_c", "QX_P_VisualCues", "QX_P_ShoppingObservation",
        "QX_P_ShoppingBarriers", "QX_F_FLASH3", "QX_Q_DESIGNAPPEAL",
        "QX_Q_PACKATT", "QX_Q_PackAtt", "QX_Q_Communication",
        "QX_Q_COMMUNICATION", "vF_IATr", "QX_Q_OA", "C_Income", "C_Education"
      ]

      return results.value.filter(r => {
        // Never flag exact matches
        if (r.status === 'exact') return false

        // We still use lowercase strictly for the QuantityPurchased check
        const dLabelLower = r.datamap_label.toLowerCase()

        // Exclude any QuantityPurchased variables
        if (dLabelLower.includes('quantitypurchased') || dLabelLower.includes('quantity_purchased')) return false

        // Always flag mapped/suggested as they require review
        if (r.status === 'mapped' || r.status === 'suggested') return true

        // For unmapped, flag them based on the specific labels/keywords
        if (r.status === 'unmapped') {
          // Use original case to match Python's case-sensitive logic
          const originalLabel = r.datamap_label 

          // 1. Check if the label exactly matches the list
          if (exactMatches.includes(originalLabel)) {
            return true
          }

          // 2. Check if the label contains any of the keywords
          if (keywords.some(keyword => originalLabel.includes(keyword))) {
            return true
          }
        }
        
        return false
      })
    })
    const usedExpectedLabels = computed(() => results.value.map(r => r.mapped_to).filter(Boolean))
    const dropdownOptions = computed(() => {
      return [...new Set(parsedExpectedLabels.value.map(l => l.label))].map(label => ({
        text: label, value: label, disabled: usedExpectedLabels.value.includes(label)
      }))
    })

    onMounted(() => initialize())

    return {
      needsPathSetup, newDecipherPath, initializeMapping, isSettingUp, decipherPath,
      isChecking, isSaving, results, statusFilter, headers, filteredResults,
      UnmappedLabels, flaggedItems, currentLoadingText, getStatusColor, getStatusIcon, exportJSON,
      editPathDialog, editPathValue, isSavingPath, hasBlockingErrors, dbMappingStatus,
      openEditPathDialog, updateDecipherPath,
      exactCount, unmappedCount, suggestedCount, mappedCount, usedExpectedLabels, onMappingChange, dropdownOptions, removeWarning, saveToDatabase,
      icons: { mdiClose, mdiDatabaseSync, mdiFolderSearchOutline, mdiPencil, mdiLinkVariant, mdiCheckCircle, mdiCodeJson, mdiRefresh, mdiContentSave, mdiAlertCircle, mdiCheckDecagram, mdiAlert, mdiFlag }
    }
  }
}
</script>

<style scoped>
.font-monospace { font-family: 'Courier New', Courier, monospace; }
.position-relative { position: relative; }
.v-dialog--fullscreen .v-card {
  display: flex;
  flex-direction: column;
}
</style>